import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Col, ConfigProvider, Grid, Row } from "antd";
import { Empty } from "antd";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useCallback } from "react";
import { createUseStyles } from "react-jss";
import { AuthContextProvider } from "../core/contexts/AuthContextProvider.jsx";
import { MetaContextProvider } from "../core/contexts/MetaContextProvider";
import { NotificationContextProvider } from "../core/contexts/NotificationContextProvider";
import globalStyle from "../globalStyles";
import { _THEME, mainColor3 } from "../theme";
import ContentWrapper from "./contentWrapper";
import { _STYLES } from "./utils/customStyles";

const { useBreakpoint } = Grid;

const { mainHeader, bodyWrap } = _STYLES;
const useStyles = createUseStyles({
  mainHeader,
  bodyWrap,
});

const Layout = ({ children }) => {
  const screens = useBreakpoint();
  const classes = useStyles();
  globalStyle();

  dayjs.extend(advancedFormat);
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchInterval: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retry: false,
      },
    },
  });

  const getResponsiveFontSize = useCallback(() => {
    if (screens.lg) {
      return 14;
    }
    if (screens.md || screens.sm) {
      return 14;
    }
    if (screens.xs) {
      return 12;
    }

    return 14;
  }, [screens.lg, screens.md, screens.sm, screens.xs]);

  const customEmpty = () => (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: "48px 24px", gap: 16,
    }}>
      {/* Inbox SVG — clean line art */}
      <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="36" cy="36" r="36" fill="#f0f6fb"/>
        <rect x="16" y="22" width="40" height="28" rx="5" stroke="#c8dff0" strokeWidth="2" fill="white"/>
        <path d="M16 38h12l3 4h10l3-4h12" stroke="#c8dff0" strokeWidth="2" strokeLinejoin="round" fill="none"/>
        <path d="M24 30h24M24 34h16" stroke="#dde8f4" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      <div style={{ textAlign: "center" }}>
        <p style={{ margin: 0, fontSize: "0.9rem", fontWeight: 600, color: "#1B2A6B" }}>
          No records found
        </p>
        <p style={{ margin: "4px 0 0", fontSize: "0.78rem", color: "#9ab8cf" }}>
          Try adjusting your filters or search terms
        </p>
      </div>
    </div>
  );

  return (
    <ConfigProvider
      renderEmpty={customEmpty}
      theme={{
        token: {
          colorPrimary: _THEME.color.primary.main,
          colorBgContainerDisabled: _THEME.color.white,
          colorTextDisabled: _THEME.color.grey.accent4,
          borderRadius: 12,
          controlHeightLG: 52,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        },
        components: {
          Button: {
            colorLink: _THEME.color.primary.main,
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
            borderRadius: 8,
            borderRadiusLG: 8,
            borderRadiusSM: 8,
          },
          Typography: {
            colorPrimary: _THEME.color.text.main,
          },
          Table: {
            headerBorderRadius: 16,
            borderColor: mainColor3,
            cellFontSize: _THEME.size.sm[3],
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          Segmented: {
            itemSelectedBg: _THEME.color.primary.main,
            itemSelectedColor: _THEME.color.white,
            //trackBg: _THEME.color.grey.accent6,
            itemColor: _THEME.color.text.main,
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          Form: {
            labelFontSize: getResponsiveFontSize(),
            itemMarginBottom: 20,
          },
          Input: {
            inputFontSize: getResponsiveFontSize(),
            inputFontSizeLG: getResponsiveFontSize(),
            inputFontSizeSM: getResponsiveFontSize(),
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
            fontSizeIcon: getResponsiveFontSize(),
            colorBgContainer: "#e8f3fc",
            colorBorder: "#c8dff0",
            addonBg: "#dff0fa",
          },
          Select: {
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          Dropdown: {
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          Tabs: {
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          DatePicker: {
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <MetaContextProvider>
          <NotificationContextProvider>
            <Row className={classes.bodyWrap}>
              <Col xs={24}>
                <AuthContextProvider>
                  <ContentWrapper>{children}</ContentWrapper>
                </AuthContextProvider>
              </Col>
              {/* <Col xs={24}>
                {isLoading ? (
                  <Loader />
                ) : isAuthenticated ? (
                  <AuthContextProvider>
                    <ContentWrapper>{children}</ContentWrapper>
                  </AuthContextProvider>
                ) : (
                  <MinimalLogin />
                )}
              </Col> */}
            </Row>
          </NotificationContextProvider>
        </MetaContextProvider>
      </QueryClientProvider>
    </ConfigProvider>
  );
};

export default Layout;
